import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import organisationService from '../services/organisationService';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatDate } from '../utils/formatters';
import './ApplicantPages.css';

const ROLE_LABELS = { admin: 'an administrator', agent: 'an agent', verifier: 'a verifier', readonly: 'a read-only member' };

// Nobody is put into an organisation without accepting: the person invited decides here.
const OrganisationInvitationsPage = () => {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setInvitations(await organisationService.myInvitations());
  }, []);

  useEffect(() => {
    load().catch(() => setNotice({ type: 'error', message: 'Your invitations could not be loaded.' }));
  }, [load]);

  const answer = async (invitation, accept) => {
    setBusy(true);
    setNotice(null);
    try {
      if (accept) {
        await organisationService.accept(invitation.organisation_id);
        // The role changes. Privileged roles then need two-factor sign-in, which the app asks for next.
        await refreshProfile();
        navigate('/organisation');
        return;
      }
      await organisationService.decline(invitation.organisation_id);
      await load();
      setNotice({ type: 'success', message: 'Invitation declined.' });
    } catch (err) {
      setNotice({ type: 'error', message: err.response?.data?.message || 'That did not work.' });
    } finally {
      setBusy(false);
    }
  };

  if (!invitations) return notice ? <Alert type={notice.type} message={notice.message} /> : <LoadingSpinner />;

  return (
    <div className="ap-page">
      <h1>Invitations</h1>
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {user?.passwordless && (
        <Alert
          type="warning"
          message="You signed in with an emailed link, so you need a password before you can join an organisation. Choose one with 'Forgot password', then come back here."
        />
      )}

      {invitations.length === 0 ? (
        <div className="ap-card">
          <p>You have no invitations waiting.</p>
          <Link to="/dashboard" className="ap-button ap-button--ghost">Back to dashboard</Link>
        </div>
      ) : (
        <ul className="ap-list">
          {invitations.map((inv) => (
            <li key={inv.organisation_id} className="ap-card ap-list__item">
              <div>
                <h2 className="ap-list__title">{inv.organisation_name}</h2>
                <p className="ap-muted">
                  You have been invited to join as {ROLE_LABELS[inv.role] || inv.role}. Invited {formatDate(inv.invited_at)}.
                  Accept only if you were expecting this.
                </p>
              </div>
              <div className="ap-list__actions">
                <button type="button" className="btn-primary ap-button" onClick={() => answer(inv, true)} disabled={busy || user?.passwordless}>Accept</button>
                <button type="button" className="ap-button ap-button--ghost" onClick={() => answer(inv, false)} disabled={busy}>Decline</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OrganisationInvitationsPage;
